import { animate, query, stagger, state, style, transition, trigger } from '@angular/animations';
import { Component, computed, inject, linkedSignal, OnInit } from '@angular/core';
import { Route } from '@angular/router';
import { AuthorizationService } from '@sphere/platform';
import { AnalyticsService } from '@sphere/platform';
import { NavigationService } from '@sphere/platform';
import AnalyticsPortalKeys from '../portal-analytics';
import { patchState, signalState } from '@ngrx/signals';
import { HomeAppCardComponent } from '../home-page/home-app-card/home-app-card.component';
import { MatGridList, MatGridTile } from '@angular/material/grid-list';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

export type DashboardState = {
    showAnimatedBackground: boolean;
    hoverApp: Route;
};

@Component({
    selector: 'sphere-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    imports: [HomeAppCardComponent, MatGridList, MatGridTile],
    animations: [
        trigger('slideInCards', [
            transition(':enter', [
                query(
                    '.plugin-card',
                    [
                        style({ transform: 'translateY(100%)', opacity: 0 }),
                        stagger('35ms', [
                            animate(
                                '180ms cubic-bezier(0.0, 0.0, 0.2, 1)',
                                style({
                                    transform: 'translateY(0%)',
                                    opacity: 1
                                })
                            )
                        ])
                    ],
                    { optional: true }
                )
            ])
        ]),
        trigger('cardHover', [
            state('*', style({ opacity: 0.4, background: `linear-gradient(0deg, {{start}} 60%, {{end}} 100%)` }), {
                params: { start: `var(--background-color-darker)`, end: 'var(--background-color-darker)' }
            }),
            state('transparent', style({ opacity: 0 })),
            transition('* <=> *', animate('300ms ease-in-out'))
        ])
    ]
})
export class DashboardComponent implements OnInit {
    private nav = inject(NavigationService);
    private analytics = inject(AnalyticsService);

    private readonly authorization = inject(AuthorizationService);
    private readonly breakpointObserver = inject(BreakpointObserver);

    public readonly state = signalState<DashboardState>({
        showAnimatedBackground: false,
        hoverApp: null
    });
    readonly hoverAppColorRaw = computed(() => {
        if (!this.state.hoverApp()) {
            return 'transparent';
        }
        return this.state.hoverApp().data.cardBackground;
    });

    readonly hoverAppColorRange = computed(() => {
        if (!this.hoverAppColorRaw() || this.hoverAppColorRaw()[0] !== '#') {
            return {
                start: 'var(--background-color-darker)',
                end: this.hoverAppColorRaw()
            };
        }
        let colorBase: string = this.hoverAppColorRaw();
        if (this.hoverAppColorRaw().length === 9) {
            colorBase = colorBase.slice(0, -2);
        }
        return {
            start: `var(--background-color-darker)`,
            end: `${colorBase}99`
        };
    });

    public readonly apps = this.authorization.getAccessibleSphereApps(this.nav.apps);

    private readonly screenSizeObserver = toSignal(
        this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge])
    );
    public readonly sphereAppGalleryColumns = linkedSignal({
        source: this.screenSizeObserver,
        computation: _res => {
            if (this.breakpointObserver.isMatched(Breakpoints.XSmall) || this.breakpointObserver.isMatched(Breakpoints.Small)) {
                return 1;
            } else if (this.breakpointObserver.isMatched(Breakpoints.Medium)) {
                return 3;
            } else if (this.breakpointObserver.isMatched(Breakpoints.Large)) {
                return 4;
            } else if (this.breakpointObserver.isMatched(Breakpoints.XLarge)) {
                return 5;
            }
            return 4;
        }
    });

    constructor() {
        this.analytics.page(AnalyticsPortalKeys.category, AnalyticsPortalKeys.pages.homeDashboard);
    }

    ngOnInit() {
        patchState(this.state, {
            hoverApp: null
        });
    }

    setShowAnimatedBackground() {
        patchState(this.state, state => ({
            showAnimatedBackground: !state.showAnimatedBackground
        }));
    }

    setHoverApp(app: Route | null) {
        patchState(this.state, {
            hoverApp: app
        });
    }
}
